import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setMobileExpanded(null);
    setActiveDropdown(null);
  }, [location.pathname]);

  const navLinks = [
    {
      name: 'Home',
      path: '/'
    },
    {
      name: 'Who We Are',
      path: '/who-we-are',
      dropdown: [
        { name: 'About Us', path: '/who-we-are', description: 'Vision, Mission, Values & Approaches' },
        { name: 'Our Story', path: '/who-we-are#story', description: 'How we began our journey' },
        { name: 'Leadership Team', path: '/who-we-are#leadership', description: 'Meet our team' },
        { name: 'Partners & Affiliations', path: '/who-we-are#partners', description: 'Who we work with' },
        { name: 'Impact & Transparency', path: '/who-we-are#transparency', description: 'Our accountability' },
        { name: 'Recognitions & Awards', path: '/who-we-are#recognitions', description: 'Awards & certifications' }
      ]
    },
    {
      name: 'Our Work',
      path: '/our-work',
      dropdown: [
        { name: 'Our Program Model', path: '/our-work#program-model', description: 'Approach, Strategy & M&E' },
        { name: 'How We Drive Change', path: '/our-work#drive-change', description: 'Our theory of change' },
        { name: 'Meal Packaging Program', path: '/meal-packaging', description: 'Our signature program', highlight: true },
        { 
          name: 'Program Pillars', 
          isGroup: true,
          items: [
            { name: 'Nourishing Lives', path: '/pillars/nourishing-lives' },
            { name: 'Empowering Communities', path: '/pillars/community-empowerment' },
            { name: 'Emergency Response', path: '/pillars/emergency-response' },
            { name: 'Growing the Movement', path: '/pillars/growing-the-movement' }
          ]
        },
        { name: 'Innovation & Sustainability', path: '/our-work#innovation', description: 'Future-forward solutions' },
        { name: 'Where We Work', path: '/our-work#where-we-work', description: 'Locations & impact map' }
      ]
    },
    {
      name: 'Join The Movement',
      path: '/join-the-movement',
      dropdown: [
        { name: 'Community Meal Packaging', path: '/join-the-movement#community-packaging', description: 'Open events for everyone' },
        { name: 'Volunteer Registration', path: '/join-the-movement#volunteer-form', description: 'Sign up to volunteer' },
        { name: 'Corporate/CSR Partnerships', path: '/join-the-movement#corporate', description: 'Partner with us' },
        { name: 'Schools & Youth Programs', path: '/join-the-movement#youth', description: 'Engage young leaders' },
        { name: 'Events & Campaigns', path: '/join-the-movement#events', description: 'Join our initiatives' },
        { name: 'Start a Fundraiser', path: '/join-the-movement#fundraiser', description: 'Rally your community' },
        { name: 'Nutrition Quiz', path: '/quiz', description: 'Test your food knowledge' },
        { name: 'Hunger Action Pledge', path: '/hunger-action-pledge', description: 'Pledge to fight hunger' },
        { name: 'Work With Us', path: '/careers', description: 'Career opportunities' }
      ]
    },
    {
      name: 'Stories & Media',
      path: '/stories-media',
      dropdown: [
        { name: 'Impact Stories', path: '/stories-media#impact-stories', description: 'Lives we\'ve touched' },
        { name: 'Blogs & Insights', path: '/stories-media#blogs-insights', description: 'Thought leadership & perspectives' },
        { name: 'News & Updates', path: '/stories-media#news-updates', description: 'Latest happenings' },
        { name: 'In the News', path: '/stories-media#in-the-news', description: 'Press coverage' },
        { name: 'Photo & Video Gallery', path: '/stories-media#gallery', description: 'Visual stories' },
        { name: 'All Blog Posts', path: '/blog', description: 'Browse all 268+ stories', highlight: true }
      ]
    },
    {
      name: 'Contact',
      path: '/contact'
    }
  ];

  const handleDropdownEnter = (index) => {
    setActiveDropdown(index);
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };

  const toggleMobileDropdown = (index) => {
    setMobileExpanded(mobileExpanded === index ? null : index);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0 max-w-[200px] sm:max-w-none">
            <img 
              src="https://customer-assets.emergentagent.com/job_meal-mission-6/artifacts/k9pkqnwr_RAHlogo_india-01.jpg"
              alt="Rise Against Hunger India"
              className="h-16 w-auto hidden sm:block"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_meal-mission-6/artifacts/o2lkxltt_MonkRAHI1.png"
              alt="RAHI"
              className="h-12 w-auto sm:hidden"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1" ref={dropdownRef}>
            {navLinks.map((link, index) => (
              <div
                key={link.path}
                className="relative"
                onMouseEnter={() => link.dropdown && handleDropdownEnter(index)}
                onMouseLeave={handleDropdownLeave}
              >
                <Link
                  to={link.path}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                    location.pathname === link.path || location.pathname.startsWith(link.path + '/')
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                >
                  {link.name}
                  {link.dropdown && (
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''}`} 
                    />
                  )}
                </Link>

                {/* Dropdown Menu */}
                {link.dropdown && activeDropdown === index && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-white rounded-lg shadow-xl border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    {link.dropdown.map((item, itemIndex) => (
                      item.isGroup ? (
                        <div key={itemIndex} className="px-4 py-2">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{item.name}</p>
                          <div className="space-y-1">
                            {item.items.map((subItem, subIndex) => (
                              <Link
                                key={subIndex}
                                to={subItem.path}
                                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-700 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                              >
                                <ChevronRight size={14} className="text-orange-400" />
                                {subItem.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <Link
                          key={itemIndex}
                          to={item.path}
                          className={`block px-4 py-3 hover:bg-orange-50 transition-colors ${
                            item.highlight ? 'bg-gradient-to-r from-orange-50 to-red-50' : ''
                          }`}
                        >
                          <p className={`text-sm font-medium ${item.highlight ? 'text-orange-600' : 'text-gray-800'}`}>
                            {item.name}
                          </p>
                          {item.description && (
                            <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                          )}
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Donate Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <Button 
              asChild
              variant="outline"
              className="border-orange-500 text-orange-600 hover:bg-orange-50 font-semibold px-5"
            >
              <Link to="/gift-a-meal">Gift a Meal</Link>
            </Button>
            <Button 
              asChild
              className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold px-6 shadow-lg hover:shadow-xl transition-all"
            >
              <Link to="/donate">Donate Now</Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:text-orange-600 hover:bg-orange-50 transition-colors flex-shrink-0"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <>
          <div className="lg:hidden fixed inset-0 top-0 bg-black/20 z-40" onClick={() => setIsOpen(false)} />
          <div className="lg:hidden absolute left-0 right-0 top-full bg-white border-t border-gray-200 shadow-xl z-50" style={{ minHeight: 'calc(100vh - 64px)', maxHeight: 'calc(100vh - 64px)', overflowY: 'auto' }}>
            <div className="px-2 pt-2 pb-3 space-y-1">
            {navLinks.map((link, index) => (
              <div key={link.path}>
                {link.dropdown ? (
                  <>
                    <button
                      onClick={() => toggleMobileDropdown(index)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-base font-medium ${
                        location.pathname.startsWith(link.path)
                          ? 'text-orange-600 bg-orange-50'
                          : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                      }`}
                    >
                      {link.name}
                      <ChevronDown 
                        size={20} 
                        className={`transition-transform duration-200 ${mobileExpanded === index ? 'rotate-180' : ''}`}
                      />
                    </button>
                    
                    {mobileExpanded === index && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-orange-200 pl-3">
                        {link.dropdown.map((item, itemIndex) => (
                          item.isGroup ? (
                            <div key={itemIndex} className="py-2">
                              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">{item.name}</p>
                              {item.items.map((subItem, subIndex) => (
                                <Link
                                  key={subIndex}
                                  to={subItem.path}
                                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                                >
                                  <ChevronRight size={14} className="text-orange-400" />
                                  {subItem.name}
                                </Link>
                              ))}
                            </div>
                          ) : (
                            <Link
                              key={itemIndex}
                              to={item.path}
                              className={`block px-3 py-2 rounded-md text-sm ${
                                item.highlight 
                                  ? 'text-orange-600 font-medium bg-orange-50' 
                                  : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                              }`}
                            >
                              {item.name}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`block px-3 py-2 rounded-md text-base font-medium ${
                      location.pathname === link.path
                        ? 'text-orange-600 bg-orange-50'
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            
            <div className="pt-2 space-y-2">
              <Button 
                asChild
                variant="outline"
                className="w-full border-orange-500 text-orange-600 hover:bg-orange-50 font-semibold"
              >
                <Link to="/gift-a-meal">Gift a Meal</Link>
              </Button>
              <Button 
                asChild
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold"
              >
                <Link to="/donate">Donate Now</Link>
              </Button>
            </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default Navbar;
